import { parseDateBR, parseTimeHHmm, combineDateTime } from './dateUtils';

/**
 * Valida período de início e fim
 * @param {Object} periodo - Objeto com dados do período
 * @param {string} periodo.data_inicio - Data de início (dd/MM/yyyy)
 * @param {string} periodo.hora_inicio - Hora de início (HH:mm)
 * @param {string} periodo.data_fim - Data de fim (dd/MM/yyyy)
 * @param {string} periodo.hora_fim - Hora de fim (HH:mm)
 * @returns {{isValid: boolean, errors: Object}} Resultado da validação
 */
export function validatePeriodo(periodo) {
    const errors = {
        data_inicio: '',
        hora_inicio: '',
        data_fim: '',
        hora_fim: ''
    };

    let dIni = null;
    let tIni = null;
    let dFim = null;
    let tFim = null;

    // Validar que data de início não está vazia
    if (!periodo.data_inicio || periodo.data_inicio.trim() === '') {
        errors.data_inicio = 'Data de início é obrigatória';
    } else {
        dIni = parseDateBR(periodo.data_inicio);
        if (!dIni) errors.data_inicio = 'Data de início inválida';
    }

    // Validar que hora de início não está vazia
    if (!periodo.hora_inicio || periodo.hora_inicio.trim() === '') {
        errors.hora_inicio = 'Hora de início é obrigatória';
    } else {
        tIni = parseTimeHHmm(periodo.hora_inicio);
        if (!tIni) errors.hora_inicio = 'Hora de início inválida';
    }

    // Validar que data de fim não está vazia
    if (!periodo.data_fim || periodo.data_fim.trim() === '') {
        errors.data_fim = 'Data de fim é obrigatória';
    } else {
        dFim = parseDateBR(periodo.data_fim);
        if (!dFim) errors.data_fim = 'Data de fim inválida';
    }

    // Validar que hora de fim não está vazia
    if (!periodo.hora_fim || periodo.hora_fim.trim() === '') {
        errors.hora_fim = 'Hora de fim é obrigatória';
    } else {
        tFim = parseTimeHHmm(periodo.hora_fim);
        if (!tFim) errors.hora_fim = 'Hora de fim inválida';
    }

    // Validar que fim é maior que início (somente se todos os campos estão válidos)
    if (dIni && tIni && dFim && tFim) {
        const inicio = combineDateTime(dIni, tIni);
        const fim = combineDateTime(dFim, tFim);

        if (fim.getTime() <= inicio.getTime()) {
            errors.hora_fim = 'Fim deve ser maior que o início';
        }
    }

    const isValid = !Object.values(errors).some(error => error !== '');

    return { isValid, errors };
}

/**
 * Valida campo obrigatório
 * @param {any} value - Valor a ser validado
 * @param {string} fieldName - Nome do campo para mensagem de erro
 * @returns {{isValid: boolean, error: string}} Resultado da validação
 */
export function validateRequired(value, fieldName = 'Campo') {
    const isValid = value !== null && value !== undefined && value !== '';
    return {
        isValid,
        error: isValid ? '' : `${fieldName} é obrigatório`
    };
}

/**
 * Valida email
 * @param {string} email - Email a ser validado
 * @returns {{isValid: boolean, error: string}} Resultado da validação
 */
export function validateEmail(email) {
    if (!email) return { isValid: true, error: '' }; // Email vazio é válido (use validateRequired se for obrigatório)

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);

    return {
        isValid,
        error: isValid ? '' : 'Email inválido'
    };
}

/**
 * Valida telefone brasileiro
 * @param {string} phone - Telefone a ser validado
 * @returns {{isValid: boolean, error: string}} Resultado da validação
 */
export function validatePhone(phone) {
    if (!phone) return { isValid: true, error: '' }; // Telefone vazio é válido

    // Remove caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');

    // Valida se tem 10 ou 11 dígitos (com DDD)
    const isValid = cleanPhone.length === 10 || cleanPhone.length === 11;

    return {
        isValid,
        error: isValid ? '' : 'Telefone inválido'
    };
}

/**
 * Valida array não vazio
 * @param {Array} array - Array a ser validado
 * @param {string} fieldName - Nome do campo para mensagem de erro
 * @returns {{isValid: boolean, error: string}} Resultado da validação
 */
export function validateArrayNotEmpty(array, fieldName = 'Lista') {
    const isValid = Array.isArray(array) && array.length > 0;
    return {
        isValid,
        error: isValid ? '' : `${fieldName} não pode estar vazia`
    };
}

/**
 * Valida se todos os campos obrigatórios estão preenchidos
 * @param {Object} data - Objeto com dados
 * @param {Array<string>} requiredFields - Array com nomes dos campos obrigatórios
 * @returns {{isValid: boolean, errors: Object}} Resultado da validação
 */
export function validateRequiredFields(data, requiredFields) {
    const errors = {};

    requiredFields.forEach(field => {
        const validation = validateRequired(data[field], field);
        if (!validation.isValid) {
            errors[field] = validation.error;
        }
    });

    const isValid = Object.keys(errors).length === 0;

    return { isValid, errors };
}
